import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Pencil } from "lucide-react";
import { fmtCurrency, fmtPct } from "../lib/formatters";
import DL from "../components/DL";
import { ScenarioName } from "../components/ResultsAtAGlance";
import { useAuth, canEdit, canEditAssociation } from "../contexts/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Screen 2 — Association: identity + assumptions + contribution summary.
//
// 2026-08-04 (Dave §4b) — Plan Year / Members / Opening Fund editable
// inline for Platform Admins and Firm Admins.
//
// 2026-08-04 (Dave §4c) — Restructured per Dave's dashboard-consistency
// mandate:
//   - LEFT panel: no more "Active Scenario" row (deleted).
//   - MIDDLE panel: renamed "Active Scenario Assumptions", now carries
//     the scenario-name chip, rows ordered top→bottom exactly like the
//     Dashboard's Scenario Variables panel (Reserve Fund Target →
//     Inflation Rate → Interest Earned → Year 1 Adjustment →
//     Adjustment Period), with Study Horizon at the very end.
//   - RIGHT panel: Contribution Summary leads with the 5 rows from the
//     Dashboard's Results-at-a-Glance panel in the same order (Year 1
//     Contribution → Year 1 Adjustment → Year 1 Budget Amount →
//     Per Member per month → Special Assessments), followed by the
//     30-year totals that were already there.
export default function AssociationScreen({ association, calc, variables, onSaved, activeScenarioName, onNameActive }) {
  const { user } = useAuth();
  const editable = canEditAssociation(user, association?.tenant_id, association?.aid);
  // Members / Opening Fund / Prior Year Contribution are also open to Analysts (2026-10-13).
  const fieldsEditable = editable || canEdit(user);

  // 2026-03-06 (Dave) — "Set as default association" moved into the user
  // pill dropdown (TopBar > UserMenu) so users can pick any association
  // as their login default without navigating to the Association tab.
  // The inline "★ Default" pill and its toggleDefault handler used to
  // live here; they were deleted in favour of the star column on each
  // dropdown row.

  // New Plan Year + Activity Audit pills now live in the blue ControlsStrip header.

  const initial = useMemo(
    () => ({
      study_year: String(association?.study_year ?? ""),
      unit_count: String(association?.unit_count ?? ""),
      opening_balance: String(association?.opening_balance ?? ""),
      prior_year_contribution: String(association?.prior_year_contribution ?? ""),
    }),
    [
      association?.aid,
      association?.study_year,
      association?.unit_count,
      association?.opening_balance,
      association?.prior_year_contribution,
    ],
  );
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // 2026-08-04 (Dave) — track Opening Fund focus so we can toggle between
  // formatted currency ($340,000) at rest and raw digits when editing.
  const [obFocused, setObFocused] = useState(false);
  // 2026-03-02 (Dave) — same currency focus toggle for Prior Year
  // Contribution (formatted $ at rest, plain digits while editing).
  const [pycFocused, setPycFocused] = useState(false);

  // Refs so the pencil affordance can focus (and select) its input on click.
  // Dave 2026-02-09 — "directly selecting the field invites editing, but the
  // pencil should also." A pencil-icon click now focuses the matching input
  // AND selects its contents so the user can just start typing.
  const membersRef = useRef(null);
  const openingFundRef = useRef(null);
  const priorContribRef = useRef(null);

  const focusMembers = () => {
    const el = membersRef.current;
    if (!el) return;
    el.focus();
    try { el.select(); } catch { /* number inputs are select()-safe */ }
  };
  const focusOpeningFund = () => {
    const el = openingFundRef.current;
    if (!el) return;
    el.focus();
    try { el.select(); } catch { /* no-op */ }
  };
  const focusPriorContrib = () => {
    const el = priorContribRef.current;
    if (!el) return;
    el.focus();
    try { el.select(); } catch { /* no-op */ }
  };

  useEffect(() => { setForm(initial); setError(null); }, [initial]);

  if (!association || !calc) return <div className="text-slate p-4">Loading…</div>;
  const V = variables || {};

  const isDirty =
    form.study_year !== initial.study_year ||
    form.unit_count !== initial.unit_count ||
    form.opening_balance !== initial.opening_balance ||
    form.prior_year_contribution !== initial.prior_year_contribution;

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    const sy = Number(form.study_year);
    const uc = Number(form.unit_count);
    const ob = Number(form.opening_balance);
    const pyc = form.prior_year_contribution === "" ? 0 : Number(form.prior_year_contribution);
    if (!Number.isInteger(sy) || sy < 1900 || sy > 2200) {
      setError("Plan Year must be a whole number between 1900 and 2200.");
      return;
    }
    if (!Number.isFinite(uc) || uc < 0 || !Number.isInteger(uc)) {
      setError("Members must be a whole number of 0 or more.");
      return;
    }
    if (!Number.isFinite(ob) || ob < 0) {
      setError("Opening Fund must be $0.00 or more.");
      return;
    }
    if (!Number.isFinite(pyc) || pyc < 0) {
      setError("Prior Year Contribution must be $0 or more.");
      return;
    }
    const patch = {};
    if (form.study_year !== initial.study_year) patch.study_year = sy;
    if (form.unit_count !== initial.unit_count) patch.unit_count = uc;
    if (form.opening_balance !== initial.opening_balance) patch.opening_balance = ob;
    if (form.prior_year_contribution !== initial.prior_year_contribution) patch.prior_year_contribution = pyc;
    if (Object.keys(patch).length === 0) return;

    setSaving(true); setError(null);
    try {
      await axios.patch(`${API}/admin/associations/${association.aid}`, patch);
      const bundleRes = await axios.get(`${API}/associations/${association.aid}`);
      if (onSaved) await onSaved(bundleRes.data);
      try {
        window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
      } catch { /* no-op */ }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => { setForm(initial); setError(null); };

  // 2026-08-04 (Dave) — borderless inline inputs, right-justified, with a
  // subtle pencil affordance next to each editable value. Opening Fund
  // renders as formatted currency ($340,000) when blurred and reverts to
  // raw digits on focus so the user can type freely.
  const editableInputCls =
    "bg-transparent border-none outline-none font-mono tabular-nums text-right text-[12px] " +
    "p-0 m-0 text-grey font-semibold focus:text-navy focus:underline focus:decoration-navy/40 " +
    "focus:decoration-1 focus:underline-offset-4 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none";

  const pencilButton = (onClick, label) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Edit ${label}`}
      title={`Edit ${label}`}
      className="shrink-0 mr-1 text-slate/50 hover:text-navy focus:text-navy focus:outline-none rounded-[3px] p-0.5"
      data-testid={`assoc-pencil-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Pencil size={10} aria-hidden="true" />
    </button>
  );

  const membersCell = fieldsEditable ? (
    <span className="inline-flex items-center justify-end gap-0.5">
      {pencilButton(focusMembers, "Members")}
      <input
        ref={membersRef}
        type="number"
        min="0"
        step="1"
        value={form.unit_count}
        onChange={(e) => set("unit_count", e.target.value)}
        className={`${editableInputCls} w-24`}
        data-testid="assoc-edit-members"
        aria-label="Members / Unit count"
      />
    </span>
  ) : association.unit_count;

  const openingFundCell = fieldsEditable ? (
    <span className="inline-flex items-center justify-end gap-0.5">
      {pencilButton(focusOpeningFund, "Opening Fund")}
      <input
        ref={openingFundRef}
        type="text"
        inputMode="decimal"
        value={
          obFocused || form.opening_balance === ""
            ? form.opening_balance
            : fmtCurrency(Number(form.opening_balance) || 0, 0)
        }
        onFocus={() => setObFocused(true)}
        onBlur={(e) => {
          setObFocused(false);
          // Sanitize $ / commas / whitespace so the stored value is a plain
          // decimal string that Number() can round-trip. Empty stays empty.
          const raw = e.target.value.replace(/[^0-9.\-]/g, "");
          set("opening_balance", raw);
        }}
        onChange={(e) => set("opening_balance", e.target.value)}
        className={`${editableInputCls} w-24`}
        data-testid="assoc-edit-opening-fund"
        aria-label="Opening Fund Balance"
      />
    </span>
  ) : fmtCurrency(association.opening_balance, 0);

  // 2026-03-02 (Dave) — Prior Year Contribution cell. Same currency
  // focus toggle as Opening Fund; formatted to zero decimals ($xxx,xxx)
  // at rest, raw digits while editing.
  const priorContribCell = fieldsEditable ? (
    <span className="inline-flex items-center justify-end gap-0.5">
      {pencilButton(focusPriorContrib, "Prior Year Contribution")}
      <input
        ref={priorContribRef}
        type="text"
        inputMode="decimal"
        value={
          pycFocused || form.prior_year_contribution === ""
            ? form.prior_year_contribution
            : fmtCurrency(Number(form.prior_year_contribution) || 0, 0)
        }
        onFocus={() => setPycFocused(true)}
        onBlur={(e) => {
          setPycFocused(false);
          const raw = e.target.value.replace(/[^0-9.\-]/g, "");
          set("prior_year_contribution", raw);
        }}
        onChange={(e) => set("prior_year_contribution", e.target.value)}
        className={`${editableInputCls} w-24`}
        data-testid="assoc-edit-prior-year-contribution"
        aria-label="Prior Year Contribution"
      />
    </span>
  ) : fmtCurrency(association.prior_year_contribution || 0, 0);

  // Plan Year is INTENTIONALLY read-only. The year the reserve study
  // is anchored to changes only via the "New Plan Year" wizard on the
  // header (2026-02-16, Slice A) or the "Reactivate" button in the
  // Plan Years drawer (2026-02-17, Slice A). Historical note: this
  // cell used to hold a disabled "coming soon" placeholder pill —
  // removed 2026-02-17 (Dave) now that both flows are live.
  const planYearCell = (
    <span data-testid="assoc-plan-year-value">{association.study_year}</span>
  );

  // Derived 5-value summary that mirrors the Dashboard's Results panel.
  // Kept as separate constants so the middle+right panels read cleanly.
  const cumulativeAdj = calc.cumulative_adjustment_signed || 0; // server-computed, signed

  return (
    <div className="grid grid-cols-2 gap-3 flex-1 overflow-auto" data-testid="screen-association">
      {/* ────────────── LEFT · Association Details ────────────── */}
      <div className="panel-compact flex flex-col relative">
        {/* Panel header row — title on the left, Save affordance on the right.
            Per Dave 2026-02-09: the Save button ONLY appears when Members
            or Opening Fund have unsaved changes; it disappears once the
            patch resolves and `form` re-matches the fresh association
            props. No always-visible toolbar. */}
        {/* Slice E5g (2026-02-09) — moved `.section-title-sm` onto the
            outer flex container so its underline stretches full-width to
            match the other two panels. Previously the class lived on an
            inner span that only spanned the "Association Details" text
            width, leaving the right-side gap without a rule. */}
        {/* Dave 2026-02-17 — header shortened from "Association Details" to
            "Details" so the pill toolbar (Members / New Plan Year / Plan Years
            / Audit) sits in a single row at the LEFT panel's ~1/3 grid width.
            `flex-nowrap` on the toolbar + `shrink-0` on each pill prevents any
            pill from wrapping onto a second line, and each button shares the
            same `h-5` height so the row reads as a clean, uniform strip. */}
        <div className="section-title-sm flex items-center justify-between gap-2">
          <span className="shrink-0" style={{ fontSize: 17 }} data-testid="association-details-heading">Association Details</span>
        </div>
        <DL
          rows={[
            ["Name", association.name],
            ["ID", `ID: ${association.tenant_display_id || "\u2014"}`],
            ["Type", association.association_type || "\u2014"],
            ["Plan Year", planYearCell],
            ["Units / Members", membersCell],
            ["Opening Fund Balance", openingFundCell],
            ["Prior Year Contribution", priorContribCell],
            ["Present Value of Expenditures over 30 Years", fmtCurrency(calc.pv_expenditures), { separator: "hairline" }],
          ]}
        />
        {fieldsEditable && isDirty && (
          <div
            className="flex items-center justify-end gap-1.5 mt-2 pt-2 border-t border-rule"
            data-testid="assoc-edit-toolbar"
          >
            <button
              onClick={reset}
              disabled={saving}
              data-testid="assoc-edit-reset"
              className="h-5 px-2 border border-rule text-slate text-[10.5px] rounded-[3px] hover:border-navy hover:text-navy disabled:opacity-40"
            >
              Reset
            </button>
            <button
              onClick={submit}
              disabled={saving}
              data-testid="assoc-edit-save"
              className="h-5 px-3 bg-navy text-white text-[10.5px] font-semibold rounded-[3px] disabled:opacity-40"
              style={{ boxShadow: saving ? "none" : "0 0 0 2px #EA6A47" }}
            >
              {saving ? "Saving\u2026" : "Save"}
            </button>
          </div>
        )}
        {fieldsEditable && error && (
          <div
            className="px-2 py-1 border border-coral bg-[#FEF5F2] text-coral text-[11px] mt-2"
            data-testid="assoc-edit-error"
          >
            {error}
          </div>
        )}
      </div>

      {/* ────────────── MIDDLE · Active Scenario Assumptions ────────────── */}
      {/* Slice E5e (2026-02-09) — heading renamed to "Active Scenario:" and
          the scenario name moved out of the ControlsStrip pill into this
          heading. Right-justified via `justify-between` so the name aligns
          vertically with the columnar values in the DL rows below. */}
      <div className="panel-compact flex flex-col" data-testid="active-scenario-assumptions">
        <div className="section-title-sm flex items-baseline justify-between gap-3">
          <span style={{ fontSize: 17 }} data-testid="active-scenario-heading">Active Scenario</span>
          <span
            className="font-mono tabular-nums text-ink font-semibold text-[12px] truncate"
            data-testid="active-scenario-name"
            title={activeScenarioName || ""}
          >
            {activeScenarioName === undefined ? "—" : <ScenarioName name={activeScenarioName} onName={onNameActive} />}
          </span>
        </div>
        {/* Rows: Reserve Fund Target → Year 1 Adjustment → Adjustment Period →
            Cumulative Adjustment → Inflation Rate → Interest Earned → Study Horizon → Required Opening Balance. */}
        <DL
          parenReserve
          rows={[
            ["Reserve Fund Target", fmtPct((V.target_pct || 0) * 100, 0)],
            ["Year 1 Adjustment", fmtPct((V.adjustment_pct || 0) * 100, 0)],
            ["Adjustment Period", `${V.adjustment_period || 0} ${V.adjustment_period === 1 ? "Year" : "Years"}`],
            [
              "Cumulative Adjustment",
              fmtCurrency(cumulativeAdj),
              { color: cumulativeAdj > 0 ? "#2E9E5B" : undefined },
            ],
            ["Inflation Rate", fmtPct((V.inflation_rate || 0) * 100, 1), { labelColor: "#3F4B57" }],
            ["Interest Earned", fmtPct((V.interest_rate || 0) * 100, 2), { labelColor: "#3F4B57" }],
            ["Study Horizon", "30 Years"],
            ["Required Opening Balance", fmtCurrency(calc.required_opening_balance), { separator: "hairline" }],
          ]}
        />
      </div>
    </div>
  );
}
